// Truths of Coaching I Wish I Knew 10 Years Ago. Short talk masterclass.
// Mux: O9kKq2bDdiOjz73nwYw1iohO1AQ8WE8MD2ir5k027sj00 (26:45)
//
// The notes are BAM's editorial summary of the talk, not a transcript.
// Only text inside quotation marks is Coleman's own words, and every
// quote carries the time he says it. talkQuotes.test.js checks each one
// against knowledge-base/transcripts/masterclass/truths-of-coaching.md
// (the talk's captions), so a quote that isn't in the talk can't ship.
// Summary copy stays in the third person so it can't read as his voice.
// No em dashes anywhere in user-visible copy.

export const TRUTHS_OF_COACHING_MODULE_KEY = "truths-of-coaching";

const VIDEO = "O9kKq2bDdiOjz73nwYw1iohO1AQ8WE8MD2ir5k027sj00";

export const TRUTHS_OF_COACHING_MODULE = {
  key: TRUTHS_OF_COACHING_MODULE_KEY,
  title: "Truths of Coaching I Wish I Knew 10 Years Ago",
  sub: "A short, direct talk on the lessons that took the longest to learn the hard way.",
  coverVideoId: VIDEO,
  duration: "27 min talk plus 10 slides",
  chapters: [
    {
      id: "watch",
      title: "Watch the talk",
      blocks: [
        { kind: "text", md:
          "27 minutes. Watch it once, then come back to the slides. Each slide is one truth, in the order Coleman walked through them on stage." },
        { kind: "text", md:
          "**About these notes.** They're BAM's summary of the talk, written from the recording. They're not a transcript. Only the lines in quotation marks are Coleman's own words, each with the time he says it in the video." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Truths of Coaching I Wish I Knew 10 Years Ago." },
      ],
    },

    {
      id: "respect-not-knowledge",
      title: "1. Respect is built on who you are, not what you know.",
      blocks: [
        { kind: "text", md:
          "\"When in reality, nobody gives a f*** until they respect you as a person.\" (0:39)" },
        { kind: "text", md:
          "Coleman describes knowing the most as a crutch he leaned on early: the most research, the most skill acquisition, the most X's and O's. Players and parents don't really care about the vocab. They care about who you are as a person: whether you're socially intelligent, trustworthy, confident, and persistent." },
        { kind: "text", md:
          "**The work most coaches skip because it's hard:**\n\n- Build social intelligence\n- Build confidence\n- Be persistent\n- Stop leaning on knowing more as a crutch" },
        { kind: "text", md:
          "It's easier to go read another book than to work on any of these. That's why so few people do." },
      ],
    },

    {
      id: "yes-then-no",
      title: "2. Say yes to everything at first. Say no to a lot more later.",
      blocks: [
        { kind: "text", md:
          "Early on you need every ounce of experience, every relationship, and as much omnipresence in your local scene as you can get. Train the kid for $20. Take the call. Help out with the workout." },
        { kind: "text", md:
          "A lot of coaches skip this step and go straight to saying no. They never build the experience that actually gives them the leverage to say no later." },
        { kind: "text", md:
          "**The signs you can start saying no:**\n\n- The work is starting to steal from your core work\n- You've identified your niche and people keep asking you to work outside it\n- You have enough value to actually be undervalued by an offer" },
        { kind: "text", md:
          "\"If you're training somebody for free, look yourself in the mirror and say, do I deserve to be making a lot of money doing this? And a lot of the time, the answer can be no, and that's fine.\" (5:52)" },
      ],
    },

    {
      id: "longer-than-you-think",
      title: "3. It's going to take longer than you think. Way longer.",
      blocks: [
        { kind: "text", md:
          "Any timeline you have in your head, expect it to take two to five times as long. \"The people who win are the people who are able to get to that doubled amount of time and still keep going and be persistent.\" (7:48)" },
        { kind: "text", md:
          "The industry is saturated. Thousands of people want to do what you do, and it's going to take a while. When you break through, you'll set a new timeline, and that one will take a while too." },
        { kind: "text", md:
          "**The shift that buys you the years:**\n\n- Fall in love with the daily work, not the destination\n- Don't fall in love with the idea of training NBA players\n- Don't fall in love with a number on a paycheck\n- Fall in love with the people you work with and the why behind it\n- Reset your timeline as many times as you need" },
        { kind: "text", md:
          "Fall in love with the end goal instead, and \"you are going to fall through quicker than you would ever imagine.\" (8:41)" },
      ],
    },

    {
      id: "no-comparison",
      title: "4. Don't compare your timeline to others.",
      blocks: [
        { kind: "text", md:
          "\"They're probably never as far as you think they are, based on social media, what you're seeing, how they're talking about themselves, and two, who gives a f*** where they are?\" (9:30)" },
        { kind: "text", md:
          "It's not about them. It's about you. The industry is saturated, and there's still more than enough room in it for everybody." },
        { kind: "text", md:
          "**A few honest reminders:**\n\n- There is more than enough room in this industry for everyone\n- The more you compare, the more you bog yourself down\n- If comparing motivates you sometimes, there's some value in that" },
        { kind: "text", md:
          "Then there's the harder version of the truth: invest a little more than you are now. Time, energy, effort, money. Coleman told the room most of them underestimate the investment it takes to get where they want to go in the timeframe they want, and that it's going to be a risk." },
      ],
    },

    {
      id: "adaptability",
      title: "5. Adaptability is the most important quality you can build.",
      blocks: [
        { kind: "text", md:
          "\"I truly believe adaptability is the number one quality of any human. And it sounds like an overstatement. However, when you think about what it means to be adaptable, it's pretty damn important.\" (10:40)" },
        { kind: "text", md:
          "Things never go according to plan. Not on the court, not in the weight room, not in business, not in life. What matters is how you adjust when they don't." },
        { kind: "text", md:
          "**Don't seek perfection. Just perform:**\n\n- Wake up on the wrong side of the bed? Perform anyway\n- Two players show up to a session? Get it done\n- The day went sideways before noon? Be a winner about it" },
        { kind: "text", md:
          "Anyone can prepare for the version that goes to plan. That version very rarely happens." },
      ],
    },

    {
      id: "problem-solving-reps",
      title: "6. See bad situations as problem-solving reps.",
      blocks: [
        { kind: "text", md:
          "Coleman calls this probably the most potent perspective shift he's made, and says he isn't perfect at it yet. When something bad happens, the first feeling is still negative. Then: \"you get a dopamine hit, oh s***, this is an opportunity for me to get better.\" (13:30)" },
        { kind: "text", md:
          "It's the same way you frame a tough drill for a player. If players are struggling in a tough environment, that's what we want. Apply it to your own life." },
        { kind: "text", md:
          "\"We talk to our players, oh, it's not going to be perfect. It's going to be chaotic. When you screw up a rep, or when you get uncomfortable, that's when you learn.\" (14:22)" },
        { kind: "text", md:
          "Then a lot of the time we go to our own lives and don't apply the same philosophy. Making that shift is one of the most important ones you can make." },
      ],
    },

    {
      id: "cant-change-game-directly",
      title: "7. You cannot change a player's game directly.",
      blocks: [
        { kind: "text", md:
          "Coleman flags this one as debatable and asked the room for pushback: \"we did not do anything directly. We did not add to a player's game directly. What we did was put them in situations where they can then change themselves.\" (15:13)" },
        { kind: "text", md:
          "\"If you have two days a week with an athlete, how much better can you really get them?\" (15:25) Use those two days to build players who can change their own game. When they're building curiosity and problem-solving with you, every time they play on their own they're improving themselves, and that's what gets them good." },
        { kind: "text", md:
          "**Your real job:**\n\n- Build curiosity\n- Build problem-solving\n- Build the way they think about the game\n- Build the qualities that let them improve when you're not in the gym" },
        { kind: "text", md:
          "Try to over-coach it and \"it actually has a reverse effect.\" (16:31) Let the ego go." },
      ],
    },

    {
      id: "workouts-are-problem-solving",
      title: "8. Workouts are problem-solving practice. Not delivery vehicles.",
      blocks: [
        { kind: "text", md:
          "You expose them to things. They go change their own game." },
        { kind: "text", md:
          "The tip you give in a workout (try to create contact on that last step) is exposure, not transformation. You did not just change their game. You gave them something they have to go work on." },
        { kind: "text", md:
          "**The standard for whether you're doing this well:** how does this player work when you are not in the gym?" },
        { kind: "text", md:
          "Walk into the gym when they're training alone. How they work should reflect how you've approached training with them. Your players should be able to work and develop on their own: when you're not there, when they go off to school, when they work with another trainer. It's different for younger players, though Coleman thinks even 14 and 15 year olds can learn it, and especially players 17 and up." },
      ],
    },

    {
      id: "environment-teaches",
      title: "9. The environment teaches. Not you.",
      blocks: [
        { kind: "text", md:
          "\"We do not teach players, the environment does.\" (19:03)" },
        { kind: "text", md:
          "Coleman asked the room who grew up playing around older guys who pushed them around, and whether they'd credit a good amount of their growth to playing with better players. His answer: most of us would. There's still teaching to do. But once you see the environment you build as the real development tool, instead of yourself and what you tell athletes, it changes everything." },
        { kind: "text", md:
          "**What that looks like in practice:**\n\n- Think of your drills as environments to learn from, not lessons\n- Build small-sided games to let players find their own solutions, not to teach them yours (a change Coleman says he made in the last couple of years)\n- Ask questions and collaborate instead of spewing information\n- Drop the ego that wants to be the source of every answer" },
        { kind: "text", md:
          "Go in only wanting them to learn what you're thinking and, in his words, \"they're going to become carbon copies of me for the most part.\" (25:55) There may be a solution that works better that you haven't even considered." },
      ],
    },

    {
      id: "basketball-is-a-language",
      title: "10. Basketball is like learning a language.",
      blocks: [
        { kind: "text", md:
          "Coleman took nine years of Spanish growing up and couldn't speak it. Then he traveled, lived in Miami, and learned it by talking to people: \"I didn't study literally one time, I just talked to people, now of course without a base of knowledge it is tough to do that.\" (23:05)" },
        { kind: "text", md:
          "School-style learning is memorize and repeat. Immersion is being put in situations where you have to form sentences and talk to people from early on. Basketball works more like immersion." },
        { kind: "text", md:
          "**What that means for your gym:**\n\n- Doing a layup 100 times the same way doesn't prepare a player to score with a seven-footer trying to block it\n- Build the foundational vocab and grammar, then get them into the conversation\n- Make them form sentences (decisions) early and often" },
        { kind: "text", md:
          "The school approach is ingrained in all of us over the first 18 years of life, and most people still coach that way. Get out of the mindset of teaching and into the mindset of building environments for players to learn." },
      ],
    },
  ],
};
